import { useMemo } from 'react';
import { Image as DatoImage } from 'react-datocms';
import { useMediaQuery } from '@carbonify/ui/theme/useMediaQuery';
import { PlusIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import InlineRichText from 'components/RichContent/InlineRichText';
import { Image } from 'services/graphql';

import styles from './ElectromobilityModule.module.css';

interface ElectromobilityModuleRecord {
  id: string;
  anchor?: string;
  __typename: string;
  title: string;
  subtitle: string;
  dealershipText: string;
  busText: string;
  factoryText: string;
  insuranceText: string;
  biomethanText: string;
  chargingText: string;
  powerText: string;
  fleetText: string;
}

type TileName = 'factory' | 'insurance' | 'biomethan' | 'charging' | 'power' | 'fleet' | 'dealership' | 'bus';

interface Tile {
  name: TileName;
  number: number;
  image: Omit<Image, 'url'>;
  anchorOffset: [top: number, left: number];
  offset: [top: number, left: number, width: number];
  elements?: {
    image: Omit<Image, 'url'>;
    offset: [top: number, left: number, width: number];
  }[];
}

const assetUrl = 'https://www.datocms-assets.com/196817/';

const centerImage: Omit<Image, 'url'> = {
  src: `${assetUrl}1784639578-center.png`,
  alt: 'Carbonify Center',
  width: 649,
  height: 437,
};

const AllTiles: Tile[] = [
  {
    name: 'factory',
    number: 1,
    image: { src: `${assetUrl}1784639578-factory.png`, alt: 'factory', width: 780, height: 780 },
    anchorOffset: [0.66, 0.5],
    offset: [-0.083, 0.331, 0.332],
    elements: [
      {
        // imgix described an issue with animated gifs on Safari, so we need to use auto=false
        // https://docs.imgix.com/en-US/apis/rendering/automatic#format
        image: { src: `${assetUrl}1784639937-conveyor-1.gif?auto=false`, alt: '', width: 220, height: 136 },
        offset: [0.656, 0.34, 0.175],
      },
    ],
  },
  {
    name: 'insurance',
    number: 2,
    image: { src: `${assetUrl}1784639578-insurance.png`, alt: 'insurance', width: 685, height: 685 },
    anchorOffset: [0.63, 0.49],
    offset: [0.11, 0.534, 0.29],
  },
  {
    name: 'biomethan',
    number: 3,
    image: { src: `${assetUrl}1784639578-biomethane.png`, alt: 'biomethan', width: 658, height: 544 },
    anchorOffset: [0.67, 0.46],
    offset: [0.33, 0.724, 0.278],
    elements: [
      {
        image: { src: `${assetUrl}1784639937-steam-1.gif?auto=false`, alt: '', width: 150, height: 150 },
        offset: [0.19, 0.067, 0.18],
      },
    ],
  },
  {
    name: 'charging',
    number: 4,
    image: { src: `${assetUrl}1784639578-charging-station.png`, alt: 'charging', width: 617, height: 617 },
    anchorOffset: [0.68, 0.47],
    offset: [0.51, 0.554, 0.258],
  },
  {
    name: 'power',
    number: 5,
    image: { src: `${assetUrl}1784639578-power-provider.png`, alt: 'power', width: 664, height: 543 },
    anchorOffset: [0.57, 0.48],
    offset: [0.665, 0.366, 0.278],
    elements: [
      {
        image: { src: `${assetUrl}1784639937-fan-2.gif?auto=false`, alt: '', width: 258, height: 258 },
        offset: [-0.01, 0.34, 0.3],
      },
      {
        image: { src: `${assetUrl}1784639937-fan-2.gif?auto=false`, alt: '', width: 258, height: 258 },
        offset: [0.145, 0.105, 0.3],
      },
    ],
  },
  {
    name: 'fleet',
    number: 6,
    image: { src: `${assetUrl}1784639578-flotte.png`, alt: 'fleet', width: 621, height: 621 },
    anchorOffset: [0.55, 0.5],
    offset: [0.46, 0.19, 0.26],
  },
  {
    name: 'dealership',
    number: 7,
    image: { src: `${assetUrl}1784639578-dealership.png`, alt: 'dealership', width: 654, height: 654 },
    anchorOffset: [0.63, 0.53],
    offset: [0.299, 0, 0.275],
  },
  {
    name: 'bus',
    number: 8,
    image: { src: `${assetUrl}1784639578-bus-1.png`, alt: 'bus', width: 678, height: 589 },
    anchorOffset: [0.67, 0.54],
    offset: [0.145, 0.168, 0.285],
  },
];

export default function ElectromobilityModule({ title, subtitle, anchor, ...props }: ElectromobilityModuleRecord) {
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const tiles = useMemo(
    () =>
      AllTiles.map(
        ({ name, number, image, offset: [top, left, width], anchorOffset: [anchorTop, anchorLeft], elements = [] }) => (
          <button
            key={name}
            type="button"
            className={clsx(styles.tile, styles[name])}
            aria-describedby={`${name}-label`}
            tabIndex={isMobile ? -1 : 0}
            style={{ '--t': top, '--l': left, '--w': width, '--at': anchorTop, '--al': anchorLeft } as object}
          >
            <span className={styles.tileImage}>
              <DatoImage
                data={image}
                className={styles.image}
              />
            </span>
            <span className={styles.hitArea} />
            <aside
              className={styles.marker}
              role="tooltip"
            >
              <PlusIcon
                className={styles.plus}
                aria-hidden
              />
              <span className={styles.number}>{number}</span>
              {!isMobile && (
                <InlineRichText
                  as="p"
                  className={styles.tooltipText}
                  id={`${name}-label`}
                >
                  {props[`${name}Text`]}
                </InlineRichText>
              )}
            </aside>
            {elements.map(({ image, offset: [top, left, width] }, index) => (
              <DatoImage
                key={`${image.src}-${index}`}
                data={image}
                className={styles.decoration}
                style={{ '--t': top, '--l': left, '--w': width } as object}
                aria-hidden
                usePlaceholder={false}
                layout="fixed"
                srcSetCandidates={[]}
              />
            ))}
          </button>
        ),
      ),
    [isMobile, props],
  );

  return (
    <section
      className={styles.module}
      id={anchor}
      data-testid="electromobility-module"
    >
      <header className={styles.header}>
        {title && (
          <InlineRichText
            as="h2"
            className={styles.title}
          >
            {title}
          </InlineRichText>
        )}

        {subtitle && (
          <InlineRichText
            as="p"
            className={styles.subtitle}
          >
            {subtitle}
          </InlineRichText>
        )}
      </header>

      <div
        className={styles.scene}
        aria-hidden={isMobile || undefined}
      >
        <div className={styles.centerTile}>
          <DatoImage
            data={centerImage}
            className={styles.image}
          />
        </div>

        {tiles}
      </div>

      <div className={styles.mobileList}>
        {AllTiles.map(({ name, number }) => (
          <article
            key={name}
            className={styles.mobileCard}
          >
            <div
              className={styles.cardNumber}
              aria-hidden="true"
            >
              {number}
            </div>
            <InlineRichText
              as="p"
              className={styles.cardText}
              id={`${name}-label`}
            >
              {props[`${name}Text`]}
            </InlineRichText>
          </article>
        ))}
      </div>
    </section>
  );
}
